import React, { useDeferredValue, useEffect, useMemo } from 'react';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { useLazyLoadQuery, useRelayEnvironment } from 'react-relay';
import { createOperationDescriptor, getRequest } from 'relay-runtime';

import { createEditorContentOperationVariables } from '@confluence/editor-content-unified-query/entry-points/createEditorContentUnifiedQueryVariables.ts';
import { clearEditorContentFetched } from '@confluence/editor-content-unified-query/entry-points/editorContentFreshness.ts';
import {
	createEditorContentFragments,
	EditorContentFragmentsContext,
} from '@confluence/editor-content-unified-query/entry-points/EditorContentFragmentsContext.ts';
import {
	EditorContentUnifiedQuery,
	type EditorContentUnifiedQueryOperation,
} from '@confluence/editor-content-unified-query/entry-points/EditorContentUnifiedQuery.graphql.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';

type EditorContentContainerProps = PropsWithChildren<{
	contentId: string;
	isLivePage: boolean;
	fallback: ReactNode;
}>;

const EditorContentReader: FC<PropsWithChildren<{ contentId: string; isLivePage: boolean }>> = ({
	contentId,
	isLivePage,
	children,
}) => {
	const environment = useRelayEnvironment();
	const draftShareId = getDraftShareIdFromQueryParam();
	// A seamless live doc transition keeps the previous document on screen instead of suspending the
	// mounted editor if the next document's data is not in the store yet.
	const deferredContentId = useDeferredValue(contentId);
	// Deferring a document already in the store would only give readers the previous one for a commit.
	const isInStore =
		environment.check(
			createOperationDescriptor(
				getRequest(EditorContentUnifiedQuery),
				createEditorContentOperationVariables({ contentId, draftShareId, isLivePage }),
			),
		).status === 'available';
	const readContentId = isInStore ? contentId : deferredContentId;
	const data = useLazyLoadQuery<EditorContentUnifiedQueryOperation>(
		EditorContentUnifiedQuery,
		createEditorContentOperationVariables({ contentId: readContentId, draftShareId, isLivePage }),
		{ fetchPolicy: 'store-or-network' },
	);

	// Leaving a document (unmount or a live doc transition) is when its server state can change
	// behind the store, e.g. by publishing, so the next entry fetches.
	useEffect(() => () => clearEditorContentFetched(readContentId), [readContentId]);

	const isLoading = readContentId !== contentId;
	const fragments = useMemo(
		() => createEditorContentFragments(data, { contentId, isLoading }),
		[data, contentId, isLoading],
	);

	return (
		<EditorContentFragmentsContext.Provider value={fragments}>
			{children}
		</EditorContentFragmentsContext.Provider>
	);
};

/**
 * Reads EditorContentUnifiedQuery once for the editor routes and provides its fragment refs, which
 * readers take with useEditorContentFragments and read with useFragment. The route preload fetches
 * the operation; this only reads it from the store or joins the in-flight request.
 */
export const EditorContentContainer: FC<EditorContentContainerProps> = ({
	contentId,
	isLivePage,
	fallback,
	children,
}) => {
	if (!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)) {
		return <>{children}</>;
	}

	return (
		<RelaySuspense fallback={fallback} interactionName="EditorContentUnifiedQuery">
			<EditorContentReader contentId={contentId} isLivePage={isLivePage}>
				{children}
			</EditorContentReader>
		</RelaySuspense>
	);
};
